import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, debounceTime, filter, of, switchMap, tap } from 'rxjs';
import {
  EmailService,
  type FeedbackRoundUser,
  type PagesReadyForReviewResult,
  ReviewAudience,
} from '@brafton/feedback-api';

/** A Project Team member offered as a recipient. */
interface StakeholderOption {
  email: string;
  name: string;
}

/** The signed-in sender, recorded in the email log. */
export interface EmailSender {
  name?: string | null;
  email?: string | null;
}

/** Loosely matches an address, just to split typed input into entries. */
const ADDRESS_SEPARATOR = /[\s,;]+/;

/**
 * The manual email dialog, opened from the project page header.
 *
 * Offers the one email type so far — "Pages Ready for Review" — for Internal or Client
 * review. Recipients are picked from the Project Team and/or typed in (non-Brafton
 * addresses allowed). As the choices change it asks the API what sending would do, and
 * shows it before anything is sent: the pages that will be listed, sites that must be
 * added to the embed allowed domains first, and every address that will be rejected
 * and why. Sending stays disabled until the API says it can go.
 */
@Component({
  selector: 'app-manual-email-dialog',
  templateUrl: './manual-email-dialog.html',
  styleUrl: './manual-email-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'onClose()' },
})
export class ManualEmailDialog {
  private readonly emails = inject(EmailService);
  private readonly destroyRef = inject(DestroyRef);

  /** Whether the dialog is shown. */
  readonly open = input(false);

  /** The project (feedback round) the email is about. */
  readonly feedbackRoundId = input('');

  /** The Project Team, offered as recipients. */
  readonly stakeholders = input<FeedbackRoundUser[]>([]);

  /** The signed-in user, recorded as the sender. */
  readonly sender = input<EmailSender | null>(null);

  /** Emitted after a send, with who it went to. */
  readonly sent = output<PagesReadyForReviewResult>();

  readonly close = output<void>();

  protected readonly ReviewAudience = ReviewAudience;

  protected readonly audience = signal<ReviewAudience>(ReviewAudience.Client);
  protected readonly selectedStakeholders = signal<ReadonlySet<string>>(new Set());
  protected readonly typedAddresses = signal<string[]>([]);
  protected readonly addressDraft = signal('');

  /** The API's answer to "what would sending do?" for the current choices. */
  protected readonly check = signal<PagesReadyForReviewResult | null>(null);
  protected readonly checking = signal(false);
  protected readonly sending = signal(false);
  protected readonly error = signal<string | null>(null);

  protected readonly stakeholderOptions = computed<StakeholderOption[]>(() =>
    this.stakeholders()
      .map((stakeholder) => ({
        email: (stakeholder.email ?? '').trim().toLowerCase(),
        name: (stakeholder.name ?? '').trim() || (stakeholder.email ?? '').trim(),
      }))
      .filter((option) => option.email.includes('@')),
  );

  /** Every chosen address, de-duplicated — what the API is asked about. */
  private readonly recipients = computed(() => [
    ...new Set([...this.selectedStakeholders(), ...this.typedAddresses()]),
  ]);

  /** Rejection wording per address, for flagging chips and team rows. */
  protected readonly rejectionByEmail = computed(() => {
    const rejections = new Map<string, string>();
    for (const rejection of this.check()?.rejected ?? []) {
      rejections.set(rejection.email, rejection.reasonLabel);
    }
    return rejections;
  });

  protected readonly canSend = computed(
    () => !this.checking() && !this.sending() && (this.check()?.canSend ?? false),
  );

  /** Why sending is blocked, in words — shown above the Send button. */
  protected readonly blockReason = computed(() => {
    const currentCheck = this.check();
    if (!currentCheck || this.checking()) {
      return null;
    }
    if (!currentCheck.pages.length) {
      return this.audience() === ReviewAudience.Internal
        ? 'No pages are marked for internal review.'
        : 'No pages are marked for client review.';
    }
    if (currentCheck.blockedDomains.length) {
      return `Add ${currentCheck.blockedDomains.join(', ')} to the embed allowed domains (the code button) before sending.`;
    }
    if (!currentCheck.accepted.length) {
      return 'Add at least one recipient who can receive this email.';
    }
    return null;
  });

  constructor() {
    // Each time it opens, start from a clean slate.
    effect(() => {
      if (this.open()) {
        this.audience.set(ReviewAudience.Client);
        this.selectedStakeholders.set(new Set());
        this.typedAddresses.set([]);
        this.addressDraft.set('');
        this.check.set(null);
        this.error.set(null);
      }
    });

    // Re-check whenever the choices change (debounced, latest wins).
    const checkRequest = computed(() => ({
      open: this.open(),
      feedbackRoundId: this.feedbackRoundId(),
      audience: this.audience(),
      recipients: this.recipients(),
    }));

    toObservable(checkRequest)
      .pipe(
        filter((request) => request.open && !!request.feedbackRoundId),
        tap(() => this.checking.set(true)),
        debounceTime(250),
        switchMap((request) =>
          this.emails
            .checkPagesReadyForReview(request.feedbackRoundId, {
              audience: request.audience,
              recipients: request.recipients,
            })
            .pipe(
              catchError(() => {
                this.error.set('Could not check this email. Please try again.');
                return of(null);
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.checking.set(false);
        this.check.set(result);
      });
  }

  protected setAudience(audience: ReviewAudience): void {
    this.audience.set(audience);
  }

  protected toggleStakeholder(email: string): void {
    this.selectedStakeholders.update((selected) => {
      const next = new Set(selected);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  }

  /** Adds whatever is typed, split on spaces, commas or semicolons. */
  protected commitDraft(): void {
    const entries = this.addressDraft()
      .split(ADDRESS_SEPARATOR)
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);

    if (entries.length) {
      this.typedAddresses.update((addresses) => [...new Set([...addresses, ...entries])]);
    }
    this.addressDraft.set('');
  }

  protected onDraftKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault();
      this.commitDraft();
    }
  }

  protected removeTyped(email: string): void {
    this.typedAddresses.update((addresses) => addresses.filter((address) => address !== email));
  }

  protected send(): void {
    if (!this.canSend()) {
      return;
    }

    this.sending.set(true);
    this.error.set(null);
    this.emails
      .sendPagesReadyForReview(this.feedbackRoundId(), {
        audience: this.audience(),
        recipients: this.recipients(),
        sentBy: this.sender(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.sending.set(false);
          this.sent.emit(result);
        },
        error: (failure: HttpErrorResponse) => {
          this.sending.set(false);
          // A 422 carries a fresh check — show what changed rather than a bare error.
          const refusal = failure.error?.data as PagesReadyForReviewResult | undefined;
          if (refusal) {
            this.check.set(refusal);
          }
          this.error.set(failure.error?.message ?? 'Could not send the email. Please try again.');
        },
      });
  }

  protected onClose(): void {
    if (this.open() && !this.sending()) {
      this.close.emit();
    }
  }
}
